import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  appointmentService,
  Appointment,
  CreateAppointmentDto,
  UpdateAppointmentDto,
  GetAppointmentsParams,
} from '../services/appointmentService';
import { queryKeys } from '../config/queryKeys';

interface UseAppointmentsParams {
  startDate?: string;
  endDate?: string;
  appointmentTypeIds?: string[];
  targetUserId?: string;
  enabled?: boolean;
}

export function useAppointments(params: UseAppointmentsParams = {}) {
  const { enabled = true, ...filters } = params;

  return useQuery({
    queryKey: queryKeys.appointments.list(filters),
    queryFn: () => appointmentService.getAppointments(filters as GetAppointmentsParams),
    enabled: enabled && !!filters.startDate && !!filters.endDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
    // Keep previous data while fetching new date range
    placeholderData: (previousData) => previousData,
  });
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.appointments.detail(id!),
    queryFn: () => appointmentService.getAppointment(id!),
    enabled: !!id,
  });
}

export function useAppointmentsByJob(jobId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.appointments.byJob(jobId!),
    queryFn: () => appointmentService.getAppointmentsByJobId(jobId!),
    enabled: !!jobId,
  });
}

export function useAppointmentsByCustomer(customerId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.appointments.byCustomer(customerId!),
    queryFn: () => appointmentService.getAppointmentsByCustomerId(customerId!),
    enabled: !!customerId,
  });
}

export function useRecentActivity() {
  return useQuery({
    queryKey: queryKeys.appointments.recentActivity(),
    queryFn: () => appointmentService.getRecentActivity(),
    staleTime: 1 * 60 * 1000, // 1 minute (matches current)
  });
}

// Mutations
export function useCreateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateAppointmentDto) =>
      appointmentService.createAppointment(data),
    onSuccess: () => {
      // Invalidate all appointment lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.recentActivity() });
    },
  });
}

export function useUpdateAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UpdateAppointmentDto) =>
      appointmentService.updateAppointment(data),
    onSuccess: (result, data) => {
      // Update the specific appointment in cache if we got the updated data back
      if (result.appointment) {
        queryClient.setQueryData(
          queryKeys.appointments.detail(data.id),
          result.appointment
        );
      }
      // Invalidate lists to refetch
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.recentActivity() });
    },
  });
}

export function useUpdateRecurringSeries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data, updateOption }: {
      id: string;
      data: UpdateAppointmentDto;
      updateOption: 'ThisInstance' | 'AllInstances'
    }) => appointmentService.updateRecurringSeries(id, data, updateOption),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.recentActivity() });
    },
  });
}

export function useUpdateRecurringInstance() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateAppointmentDto }) =>
      appointmentService.updateRecurringInstance(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.recentActivity() });
    },
  });
}

export function useDeleteAppointment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, instanceDate }: { id: string; instanceDate?: Date }) =>
      appointmentService.deleteAppointment(id, instanceDate),
    onSuccess: (_, { id }) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: queryKeys.appointments.detail(id) });
      // Invalidate lists
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.recentActivity() });
    },
  });
}

export function useDeleteRecurringSeries() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, deleteOption }: { id: string; deleteOption: 'ThisInstance' | 'AllInstances' }) =>
      appointmentService.deleteRecurringSeries(id, deleteOption),
    onSuccess: (_, { id }) => {
      queryClient.removeQueries({ queryKey: queryKeys.appointments.detail(id) });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.lists() });
      queryClient.invalidateQueries({ queryKey: queryKeys.appointments.recentActivity() });
    },
  });
}
